import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Funnel } from 'lucide-react';
import { PageHeader } from '@features/property-management/components/PageHeader';
import { DataState, SegmentedControl, TableRowsSkeleton, Text } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/components/basic/EmptyState';
import { ListingTable } from '@/shared/ui/components/basic/listing-table';
import { useAuth } from '@/context/authentication';
import { DealsService } from '@/services/requests/housien-services/DealsService';
import { CreateDealDialog, type CreateDealFormData } from '@features/deals/components/CreateDealDialog';
import { LoseDealDialog } from '@features/deals/components/LoseDealDialog';
import { DealMobileCard } from '@features/deals/components/DealMobileCard';
import { buildDealColumns, type DealRow } from '@features/deals/pages/deal-table-columns';
import { cn } from '@/shared/ui/utils/tailwind';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { toast } from 'sonner';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { MoveStageSheet } from '@/shared/ui/components/composed/mobile-sheet';
import { MobileLayoutSwitcher } from '@/shared/ui/components/composed/mobile-layout-switcher';
import { RowsIcon, ViewGridIcon, ViewHorizontalIcon } from '@/shared/ui/radix-icons';
import { dealAccentForStage } from '@features/deals/utils/deal-state';
import { AttendancesMobileBoard, type MobileBoardLayout } from '../components/AttendancesMobileBoard';
import { moveRefusalMessage } from '../utils/move-refusal';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { AttendanceColumn } from '../components/AttendanceColumn';
import { AttendancesFilterPanel } from '../components/AttendancesFilterPanel';
import { SEARCH_PAGE_SIZE, useAttendances, type Attendance } from '../hooks/useAttendances';
import { useAttendanceFilters } from '../hooks/useAttendanceFilters';
import {
  ATTENDANCE_COLUMNS,
  isFunnelColumn,
  columnForStage,
  useAttendanceColumnLabel,
  type AttendanceColumnId,
} from '../constants/attendance-columns';
import { navIconFor } from '@layout/agency/agency-nav';

type AttendanceView = 'board' | 'table';

/**
 * Cards shown in Perdido and Pausados before the rest folds away.
 *
 * Two is enough to say the column is not empty and what is in it.
 * These two grow without bound and are read rarely, so at full height
 * they pushed the funnel's own columns off the screen.
 */
const OUT_OF_PLAY_PREVIEW = 2;

/**
 * Atendimentos: every opportunity, in the two shapes one is worked in.
 *
 * The board groups by stage and is where the day happens; the table is
 * for values, sorting, and the ones the board has no column for. They
 * are two readings of a single query, not two areas -- clicking either
 * lands on the same detail, which is what made a separate Negócios area
 * a second door to one room.
 */
export function AttendancesPage() {
  const { agency_id: agencyId = '' } = useParams<{ agency_id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('features/attendances');
  const tDeals = useTranslation('features/deals').t;
  const auth = useAuth();
  const brokerEmail = (auth.user?.profile as { email?: string })?.email;

  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get('view') as AttendanceView) ?? 'board';
  /* Its own parameter, so switching layout on a phone does not rewrite the
     board/table choice a desktop shares the URL with. */
  const mobileLayout = (searchParams.get('layout') as MobileBoardLayout) ?? 'tabs';
  const [showFilters, setShowFilters] = useState(false);
  /** The attendance waiting on a loss reason after being dropped. */
  const [losing, setLosing] = useState<string | null>(null);
  const isMobile = useIsMobile();
  /** The attendance whose stage the phone is choosing. */
  const [moving, setMoving] = useState<Attendance | null>(null);

  const { agency } = useAgency((state) => state);
  // Keyed by e-mail, not by `m.id`: the attendance stores its broker as
  // an e-mail, so the uuid this used to send matched nothing and the
  // corretor filter always came back empty. The detail screen builds the
  // same BrokerOption from `m.email` -- this is that.
  const brokers = useMemo(
    () => (agency.members || []).map(
      (m) => ({ id: m.email, name: `${m.first_name} ${m.last_name}`.trim() || m.email }),
    ),
    [agency.members],
  );

  const {
    filters, activeFilters, removeFilter, clearAll, apply,
  } = useAttendanceFilters(brokers);

  // Page per column, owned here so loading more in one column leaves
  // the other four alone.
  const [pages, setPages] = useState<Partial<Record<AttendanceColumnId, number>>>({});
  const searching = search.trim().length > 0;

  const handleLoadMore = useCallback((columnId: AttendanceColumnId) => {
    setPages((prev) => ({ ...prev, [columnId]: (prev[columnId] ?? 1) + 1 }));
  }, []);

  const {
    isLoading, isError, columns, deals, searchTruncated, refresh,
  } = useAttendances(
    agencyId, filters, pages, handleLoadMore, searching,
  );

  const prefilledClientId = searchParams.get('clientId');
  const [createOpen, setCreateOpen] = useState(Boolean(prefilledClientId));

  const matches = useCallback((name: string, property: string) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return name.toLowerCase().includes(query) || property.toLowerCase().includes(query);
  }, [search]);

  // The column total stays the server's unless a text search is active,
  // in which case it reports what matched. Overriding it unconditionally
  // would show the page size where the total belongs, which is the bug
  // this etapa fixes.
  const filteredColumns = useMemo(() => columns.map((column) => {
    if (!searching) return column;
    const attendances = column.attendances.filter(
      (a) => matches(a.clientName, a.propertyTitle),
    );
    return { ...column, attendances, count: attendances.length, hasMore: false };
  }), [columns, matches, searching]);

  const filteredDeals = useMemo(() => deals.filter(
    (d) => matches(d.client_name ?? d.client_id ?? '', d.property_title ?? d.property_id ?? ''),
  ), [deals, matches]);

  const totalCount = filteredDeals.length;

  /**
   * Moves an attendance to the column it was dropped on.
   *
   * The funnel columns move it straight: the API allows any running
   * stage to reach any other, on the reasoning that a move by hand is a
   * fact correction. `pausados` and `encerrado` are decisions rather
   * than positions, so they take the path that collects what the
   * decision needs -- pause has nothing to ask, a loss has its reason.
   *
   * `ganho` is the one that cannot be completed from here: a win is an
   * accepted offer with a value, and inventing one on a drop would open
   * a Venda or Locação with no number in it. The drop opens the
   * attendance with that form already up, so the gesture still leads
   * somewhere instead of dead-ending on an unchanged screen.
   */
  const handleDropOn = useCallback(async (
    columnId: AttendanceColumnId, dealId: string, fromStage?: string,
  ) => {
    // Dropping a card back where it already sits is not a move. Without
    // this it still wrote: the attendance got a stage_changed event and
    // its idle line reset to "movido hoje", so the board reported
    // activity the broker never performed.
    const landing = ATTENDANCE_COLUMNS.find((column) => column.id === columnId);
    if (fromStage && landing?.stages.some((stage) => stage === fromStage)) return;

    if (columnId === 'ganho') {
      // Carrying `?win=1` rather than just landing on the attendance: the
      // drop *is* the broker saying they closed it, and arriving on a
      // screen that looks unchanged reads as the drop having failed.
      navigate(`/app/agencies/${agencyId}/deals/${dealId}?win=1`);
      return;
    }
    if (columnId === 'pausados') {
      try {
        await DealsService.pauseDeal(agencyId, dealId);
        refresh();
      } catch (error) {
        notifications.show({ color: 'red', message: moveRefusalMessage(error, fromStage, t) });
      }
      return;
    }
    if (columnId === 'encerrado') {
      setLosing(dealId);
      return;
    }
    const target = ATTENDANCE_COLUMNS.find((column) => column.id === columnId);
    const stage = target?.stages[0];
    if (!stage) return;
    try {
      await DealsService.advanceDeal(agencyId, dealId, { target_stage: stage });
      refresh();
    } catch (error) {
      // The server refuses moves the attendance has not earned -- an
      // offer stage with no property, a win with no accepted offer --
      // and says exactly why. Swallowing it left the card snapping back
      // with no explanation, which reads as the board being broken
      // rather than as the move being refused.
      notifications.show({ color: 'red', message: moveRefusalMessage(error, fromStage, t) });
    }
  }, [agencyId, navigate, refresh, t]);

  /** How long the undo stays reachable, in ms. */
  const UNDO_MS = 4500;
  const columnLabel = useAttendanceColumnLabel();

  /**
   * Moves an attendance from the phone's stage sheet, and offers to undo it.
   *
   * Undo is not a second concept: it replays the same move in the opposite
   * direction through the same handler, so a move the server would refuse on
   * the way back is refused out loud rather than silently leaving the card
   * where it was not meant to be. The offer lasts a few seconds because that
   * is how long it takes to notice the card left the column — long enough to
   * catch a mis-tap, short enough not to sit on the screen.
   */
  const handleMoveFromSheet = useCallback(async (
    attendance: Attendance, targetColumn: AttendanceColumnId,
  ) => {
    const fromColumn = columnForStage(attendance.stage);
    setMoving(null);
    await handleDropOn(targetColumn, attendance.dealId, attendance.stage);

    const landed = ATTENDANCE_COLUMNS.find((column) => column.id === targetColumn);
    // Ganho and Encerrado hand off to a dialog or another screen; there is
    // nothing completed to undo yet, so no offer is made.
    if (!fromColumn || targetColumn === 'ganho' || targetColumn === 'encerrado') return;

    toast(
      t('move.moved', {
        name: (attendance.clientName || t('card.unnamedClient')).split(' ')[0],
        stage: columnLabel(targetColumn),
      }),
      {
        duration: UNDO_MS,
        action: {
          label: t('move.undo'),
          onClick: () => {
            void handleDropOn(fromColumn, attendance.dealId, landed?.stages[0]);
          },
        },
      },
    );
  }, [handleDropOn, t, columnLabel]);

  const funnelColumns = filteredColumns.filter((column) => isFunnelColumn(column.id));
  const outOfPlayColumns = filteredColumns.filter((column) => !isFunnelColumn(column.id));

  const setParam = (key: string, value: string) => {
    setSearchParams((params) => {
      if (value) params.set(key, value);
      else params.delete(key);
      return params;
    });
  };

  const closeCreateDialog = useCallback(() => {
    setCreateOpen(false);
    if (prefilledClientId) setParam('clientId', '');
  }, [prefilledClientId]);

  const handleCreateSubmit = useCallback(async (data: CreateDealFormData) => {
    await DealsService.createDeal(agencyId, {
      interest: data.interest,
      broker_id: brokerEmail ?? '',
      client_id: data.client_id || undefined,
      property_id: data.property_id || undefined,
      notes: data.notes || undefined,
    });
    closeCreateDialog();
    refresh();
  }, [agencyId, brokerEmail, closeCreateDialog, refresh]);

  const tableColumns = useMemo(() => buildDealColumns(tDeals), [tDeals]);
  const tableData: DealRow[] = useMemo(
    () => filteredDeals.map((d) => ({ ...d, id: d.deal_id })),
    [filteredDeals],
  );

  const hasActiveQuery = Boolean(search.trim()) || activeFilters.length > 0;

  return (
    <div className="flex flex-1 min-h-0 w-full flex-col overflow-hidden bg-[var(--surface-secondary)] font-[family-name:var(--font-family)]">
      <div className="flex flex-1 min-h-0 w-full flex-col gap-[var(--space-4)] overflow-auto p-[var(--space-5)] px-[var(--space-6)]">
        <PageHeader
          title={t('pageTitle')}
          icon={navIconFor('attendances', 20)}
          ctaLabel={t('cta')}
          onCtaClick={() => setCreateOpen(true)}
          searchPlaceholder={t('searchPlaceholder')}
          searchValue={search}
          onSearchChange={setSearch}
          onFiltersToggle={() => setShowFilters((open) => !open)}
          filterCount={activeFilters.length}
          activeFilters={activeFilters}
          onRemoveFilter={removeFilter}
          onClearAllFilters={clearAll}
          introLabel={t('intro.label')}
          introBody={t('intro.body')}
          introTestIdPrefix="attendances"
        />

        {/*
          The count sits on the toggle's row rather than on a line of its
          own: it qualifies what the two views are showing, and as a
          separate line it read as a stray caption above the board.
        */}
        <div className="flex flex-wrap items-center justify-between gap-(--space-3)">
          <div className="flex flex-wrap items-baseline gap-(--space-2)">
            <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary)">
              {t('resultsCount', { count: totalCount })}
            </Text>
            {/* A cap nobody is told about reads as "we looked at
                everything". The search runs in the browser over a wide
                page, so a busier column than that is only partly
                searched — and the filters, which are server-side, are
                the way to narrow it properly. */}
            {searchTruncated && (
              <Text
                component="span"
                data-testid="attendances-search-truncated"
                className="text-[length:var(--text-meta)] text-(--accent-cobre)"
              >
                {t('searchTruncated', { limit: SEARCH_PAGE_SIZE })}
              </Text>
            )}
          </div>
          {/*
            * The phone gets the layouts, not the two desktop views.
            *
            * "Quadro" and "Tabela" are a desktop distinction: at 400px the
            * table is six columns of text nobody can read, so it had been
            * standing in for a flat list under a name that promised a table.
            * These are the three Visitas already offers on a phone, named
            * for what they do to the cards.
            *
            * Visitas has a fourth button, its table. Atendimentos does not,
            * because its table on a phone *is* the flat list — two buttons
            * rendering the same screen is a worse answer than three honest
            * ones.
            */}
          {isMobile ? (
            <MobileLayoutSwitcher<MobileBoardLayout>
              value={mobileLayout}
              onChange={(next) => setParam('layout', next)}
              ariaLabel={t('views.layoutSwitcher')}
              testId="attendances-view-toggle"
              options={[
                { value: 'tabs', label: t('views.tabs'), icon: <ViewHorizontalIcon width={14} height={14} /> },
                { value: 'stack', label: t('views.list'), icon: <RowsIcon width={14} height={14} /> },
                { value: 'carousel', label: t('views.carousel'), icon: <ViewGridIcon width={14} height={14} /> },
              ]}
            />
          ) : (
            <SegmentedControl
              value={view}
              onChange={(v) => setParam('view', v)}
              items={[
                { value: 'board', label: t('views.board') },
                { value: 'table', label: t('views.table') },
              ]}
              size="sm"
              testId="attendances-view-toggle"
            />
          )}
        </div>

        <DataState
          isLoading={isLoading}
          isError={isError}
          isEmpty={false}
          loadingFallback={<TableRowsSkeleton rows={8} />}
          onRetry={refresh}
          errorMessage={t('loadError')}
        >
          {isMobile ? (
            /* One stage at a time, every stage at once, or a swipe
               between columns — whichever the layout switch above picked. */
            <AttendancesMobileBoard
              agencyId={agencyId}
              layout={mobileLayout}
              columns={filteredColumns.map((column) => ({
                id: column.id,
                label: columnLabel(column.id),
                count: column.count,
                items: column.attendances,
                stage: ATTENDANCE_COLUMNS.find((c) => c.id === column.id)!.stages[0],
              }))}
              onMoveStage={setMoving}
            />
          ) : view === 'board' ? (
            <div data-tour="attendances-board" className="flex flex-col gap-(--space-5) lg:flex-row lg:items-start lg:gap-(--space-4)">
              {/*
                Two groups, not one row. Perdido and Pausados are not
                steps of the funnel -- they are work out of play -- and
                standing them beside Novos..Ganho made the board read as
                a funnel that ends in a loss.
              */}
              <div
                className="flex flex-1 flex-col gap-(--space-5) md:grid md:items-start md:gap-(--space-4)"
                style={{ gridTemplateColumns: `repeat(${funnelColumns.length}, 1fr)` }}
              >
                {funnelColumns.map((column) => (
                  <AttendanceColumn
                    key={column.id}
                    agencyId={agencyId}
                    column={column}
                    onDropAttendance={(dealId, fromStage) => { void handleDropOn(column.id, dealId, fromStage); }}
                  />
                ))}
              </div>

              {outOfPlayColumns.length > 0 && (
                <div
                  data-testid="attendances-out-of-play"
                  // Stacked, not side by side: these two are a short
                  // aside, and a second across costs the funnel width
                  // for columns nobody works from.
                  className={cn(
                    'flex flex-col items-stretch gap-(--space-4)',
                    'lg:shrink-0 lg:basis-[clamp(13rem,20%,18rem)]',
                    'lg:border-l lg:border-(--border-subtle) lg:pl-(--space-4)',
                  )}
                >
                  {outOfPlayColumns.map((column) => (
                    <AttendanceColumn
                      key={column.id}
                      agencyId={agencyId}
                      column={column}
                      onDropAttendance={(dealId, fromStage) => { void handleDropOn(column.id, dealId, fromStage); }}
                      previewLimit={OUT_OF_PLAY_PREVIEW}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : filteredDeals.length === 0 ? (
            <EmptyState
              icon={navIconFor('attendances', 28)}
              title={hasActiveQuery ? t('list.noResults.title') : t('list.empty.title')}
              description={hasActiveQuery ? t('list.noResults.description') : t('list.empty.description')}
              action={hasActiveQuery ? undefined : { label: t('cta'), onClick: () => setCreateOpen(true) }}
            />
          ) : (
            <>
              <div className="hidden lg:block overflow-auto rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-[var(--shadow-sm)]">
                <ListingTable
                  data={tableData}
                  columns={tableColumns}
                  onRowClick={(d) => navigate(`/app/agencies/${agencyId}/deals/${d.deal_id}`)}
                  testId="attendances-listing-table"
                />
              </div>
              <div className="flex lg:hidden flex-col gap-(--space-3)">
                {filteredDeals.map((deal) => (
                  <DealMobileCard key={deal.deal_id} deal={deal} agencyId={agencyId} />
                ))}
              </div>
            </>
          )}
        </DataState>

        {/*
          Keyed by the applied filters so removing a chip in the header
          re-seeds the panel: without it the panel would reopen showing
          the selection the user just took off.
        */}
        <AttendancesFilterPanel
          key={`${filters.originChannel}|${(filters.stages ?? []).join(',')}|${filters.brokerId}`}
          open={showFilters}
          onClose={() => setShowFilters(false)}
          onApply={apply}
          onClear={clearAll}
          initialFilters={filters}
          brokers={brokers}
        />

        {/*
          The phone's replacement for dragging a card between columns. The
          funnel stages are the list; Ganho, Pausar and Abandonar sit below
          the divider because they are decisions rather than positions, and
          each opens the flow that already collects what the decision needs.
        */}
        <MoveStageSheet
          open={Boolean(moving)}
          subjectName={moving?.clientName || t('card.unnamedClient')}
          subjectDetail={moving?.propertyTitle || t('card.noProperty')}
          currentId={(moving && columnForStage(moving.stage)) || ''}
          /*
            The running stages only. "Ganho" is a funnel column on the board,
            but here it belongs with Pausar and Abandonar below the divider:
            a win is an accepted offer with a value attached, so it opens the
            attendance rather than writing a stage — and offering it twice,
            once as a position and once as a decision, invites the reader to
            wonder which of the two does something different.
          */
          options={ATTENDANCE_COLUMNS
            .filter((column) => isFunnelColumn(column.id) && column.id !== 'ganho')
            .map((column) => ({
              id: column.id,
              label: columnLabel(column.id),
              hint: t(`move.hints.${column.id}`, { defaultValue: '' }) || undefined,
              color: `var(--accent-${dealAccentForStage(column.stages[0])})`,
            }))}
          actions={moving ? [
            {
              id: 'won',
              label: t('move.actions.win'),
              tone: 'var(--status-success-text)',
              onSelect: () => { void handleMoveFromSheet(moving, 'ganho'); },
            },
            {
              id: 'paused',
              label: t('move.actions.pause'),
              tone: 'var(--text-secondary)',
              onSelect: () => { void handleMoveFromSheet(moving, 'pausados'); },
            },
            {
              id: 'lost',
              label: t('move.actions.abandon'),
              tone: 'var(--status-error-text)',
              onSelect: () => { void handleMoveFromSheet(moving, 'encerrado'); },
            },
          ] : undefined}
          onSelect={(columnId) => {
            if (moving) void handleMoveFromSheet(moving, columnId as AttendanceColumnId);
          }}
          onClose={() => setMoving(null)}
        />

        <LoseDealDialog
          open={Boolean(losing)}
          onClose={() => setLosing(null)}
          onSubmit={async (reason, note) => {
            if (losing) {
              await DealsService.advanceDeal(agencyId, losing, {
                target_stage: 'lost',
                lost_reason: reason,
                lost_note: note,
              });
            }
            setLosing(null);
            refresh();
          }}
        />

        <CreateDealDialog
          open={createOpen}
          onClose={closeCreateDialog}
          onSubmit={handleCreateSubmit}
          agencyId={agencyId}
          initialClientId={prefilledClientId}
        />
      </div>
    </div>
  );
}

export default AttendancesPage;
